/**
 * Owner: Masa Bayu
 * File: author-sales.ts
 * Date:Time: 2026-09-16 14:41
 * Description: T6-E4 SaleRecord projections from confirmed Commerce Orders only.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { authorizeAuthorSurfaceAccess } from './author-account';
import { assertT6E1SecurityIntact, assertT6E4SecurityIntact } from './author-security';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
export const QXREAD_SALE_RECORD_STATES = ['recorded', 'adjusted', 'void'];
function assertSaleState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_SALE_RECORD_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_SALE_RECORD_STATES.join(', ')}`);
    }
    return value;
}
function assertPositiveInt(value, label) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
        throw new Error(`${label} must be a positive integer`);
    }
    return value;
}
function assertNonNegativeInt(value, label) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw new Error(`${label} must be a non-negative integer`);
    }
    return value;
}
/**
 * Project SaleRecords from confirmed Orders only. Pending/failed payment never enters here.
 */
export function projectSaleRecordsFromConfirmedOrders(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead sale projection');
    assertT6E1SecurityIntact();
    assertT6E4SecurityIntact();
    assertValidId(input.authorAccount.authorAccountId, 'QXRead authorAccountId');
    const recordedAt = input.recordedAt ?? new Date().toISOString();
    assertTimestamp(recordedAt, 'QXRead sale recordedAt');
    const records = [];
    for (const order of input.confirmedOrders) {
        for (const line of order.lines) {
            if (!input.authorOwnedPublicationIds.has(line.publicationId)) {
                continue;
            }
            const quantity = assertPositiveInt(line.quantity, 'QXRead sale quantity');
            const sellerSubtotalMinorUnits = assertNonNegativeInt(line.sellerPriceMinorUnits * quantity, 'QXRead sellerSubtotalMinorUnits');
            records.push(Object.freeze({
                saleRecordId: createQxreadId('qxrdsale'),
                authorAccountId: input.authorAccount.authorAccountId,
                accountId: input.authorAccount.accountId,
                orderId: order.orderId,
                publicationId: line.publicationId,
                title: line.title,
                quantity,
                sellerSubtotalMinorUnits,
                currency: line.currency,
                state: assertSaleState('recorded', 'QXRead sale state'),
                recordedAt: order.confirmedAt,
                derivedFromConfirmedOrder: true,
                inventedByAuthorUi: false,
            }));
        }
    }
    return Object.freeze(records);
}
export function projectAuthorSalesSummary(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead sales summary');
    assertT6E4SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    const projectedAt = input.projectedAt ?? new Date().toISOString();
    assertTimestamp(projectedAt, 'QXRead sales projectedAt');
    const owned = input.saleRecords.filter((record) => record.authorAccountId === input.authorAccount.authorAccountId && record.state !== 'void');
    let unitsSold = 0;
    let sellerSubtotalMinorUnits = 0;
    let currency = typeof input.currency === 'string' && input.currency.length > 0 ? input.currency : 'USD';
    for (const record of owned) {
        unitsSold += record.quantity;
        sellerSubtotalMinorUnits += record.sellerSubtotalMinorUnits;
        currency = record.currency;
    }
    return Object.freeze({
        authorAccountId: input.authorAccount.authorAccountId,
        accountId: input.authorAccount.accountId,
        saleCount: owned.length,
        unitsSold,
        sellerSubtotalMinorUnits,
        currency,
        projectedAt,
        accessAllowed: access.allowed,
        accessReason: access.reason,
    });
}
//# sourceMappingURL=author-sales.js.map